// src/assessment2/EvaluationComparisonTabs.js — Assessment 2 (PLAN_ASSESSMENT2.md): sin psicolaboral ni Mapa de Talentos.
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    Alert,
    Avatar,
    Checkbox
} from '@mui/material';
import {
    Search as SearchIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    Map as MapIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    Legend,
    ResponsiveContainer
} from 'recharts';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';

// Componentes estilizados
const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[8],
    },
}));

const ScoreBar = styled(LinearProgress)(({ theme }) => ({
    height: 8,
    borderRadius: 4,
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
        borderRadius: 4,
    },
}));

const CompetencyBar = styled(LinearProgress)(({ theme }) => ({
    height: 12,
    borderRadius: 6,
    backgroundColor: theme.palette.grey[200],
    '& .MuiLinearProgress-bar': {
        borderRadius: 6,
    },
}));

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`comparison-tabpanel-${index}`}
            aria-labelledby={`comparison-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

const EvaluationComparisonTabs = ({
    candidatesData,
    competenciesData,
    evaluationResponses
}) => {
    const [tabValue, setTabValue] = useState(0);
    const [candidateTabValue, setCandidateTabValue] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCandidates, setSelectedCandidates] = useState([]);
    const [selectedCompetencies, setSelectedCompetencies] = useState(competenciesData.map(c => c.id));
    const [comparisonTabValue, setComparisonTabValue] = useState(0);
    const [sortBy, setSortBy] = useState('candidate');
    const [sortDirection, setSortDirection] = useState('asc');

    const { language } = useStore();
    const { t } = useTranslation();

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleCandidateTabChange = (event, newValue) => {
        setCandidateTabValue(newValue);
    };

    const handleComparisonTabChange = (event, newValue) => {
        setComparisonTabValue(newValue);
    };

    // Datos filtrados para mostrar - candidatos completados con response
    const completedCandidatesForRanking = React.useMemo(() => {
        // Assessment 2: orden por score del assessment; desempate estable por nombre
        return (candidatesData || [])
            .filter(c => c.status === 'completed' && c.hasResponse)
            .sort((a, b) => {
                const aOverall = Number(a.overallScore || 0);
                const bOverall = Number(b.overallScore || 0);

                if (bOverall !== aOverall) return bOverall - aOverall;

                return String(a.name || '').localeCompare(String(b.name || ''));
            });
    }, [candidatesData, evaluationResponses]);

    // Inicializar candidatos seleccionados con los primeros 2 candidatos completados
    useEffect(() => {
        if (completedCandidatesForRanking.length > 0 && selectedCandidates.length === 0) {
            setSelectedCandidates(completedCandidatesForRanking.slice(0, 2).map(c => c.id));
        }
    }, [candidatesData]);

    // Inicializar competencias seleccionadas
    useEffect(() => {
        setSelectedCompetencies(competenciesData.map(c => c.id));
    }, [competenciesData]);

    // Función para manejar el sorting de la tabla
    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortDirection('asc');
        }
    };

    // Candidatos ordenados para la tabla
    const sortedCandidatesForTable = React.useMemo(() => {
        let sortedCandidates = [...completedCandidatesForRanking];

        if (sortBy === 'candidate') {
            sortedCandidates.sort((a, b) => {
                const comparison = a.name.localeCompare(b.name);
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        } else if (sortBy === 'total') {
            sortedCandidates.sort((a, b) => {
                const comparison = a.overallScore - b.overallScore;
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        } else {
            // Ordenar por competencia específica
            const competencyId = sortBy;
            sortedCandidates.sort((a, b) => {
                const scoreA = a.competencyScores[competencyId] || 0;
                const scoreB = b.competencyScores[competencyId] || 0;
                const comparison = scoreA - scoreB;
                return sortDirection === 'asc' ? comparison : -comparison;
            });
        }

        return sortedCandidates;
    }, [completedCandidatesForRanking, sortBy, sortDirection]);

    // Componente para encabezados clickeables con indicador de sorting
    const SortableTableCell = ({ column, children, align = 'left' }) => {
        const isActive = sortBy === column;
        const isAsc = sortDirection === 'asc';

        return (
            <TableCell
                align={align}
                onClick={() => handleSort(column)}
                sx={{
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'grey.50' },
                    fontWeight: isActive ? 'bold' : 'normal',
                    position: 'relative'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
                    {children}
                    <Box sx={{ ml: 1, display: 'flex', flexDirection: 'column', minWidth: 16 }}>
                        {isActive ? (
                            isAsc ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />
                        ) : (
                            <Typography variant="caption" color="text.secondary">↑↓</Typography>
                        )}
                    </Box>
                </Box>
            </TableCell>
        );
    };

    // Manejar selección de candidatos
    const handleCandidateSelection = (candidateId) => {
        setSelectedCandidates(prev => {
            if (prev.includes(candidateId)) {
                return prev.filter(id => id !== candidateId);
            } else {
                return [...prev, candidateId];
            }
        });
    };

    // Manejar selección de competencias
    const handleCompetencySelection = (competencyId) => {
        setSelectedCompetencies(prev => {
            if (prev.includes(competencyId)) {
                return prev.filter(id => id !== competencyId);
            } else {
                return [...prev, competencyId];
            }
        });
    };

    // Función para renderizar el análisis de un candidato
    const renderCandidateAnalysis = (candidate) => {
        const candidateResponse = evaluationResponses.find(r => r.applicantId === candidate.id);

        if (!candidateResponse) {
            return (
                <Alert severity="warning">
                    {t('evaluationResponses.noAnalysisDataAvailable')}
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        {t('evaluationResponses.applicantId')}: {candidate.id}
                    </Typography>
                </Alert>
            );
        }

        const overallScore = candidate.overallScore || 0;

        // Función auxiliar para obtener color según score
        const getScoreColor = (score) => {
            if (score >= 85) return 'success.main';
            if (score >= 70) return 'warning.main';
            return 'error.main';
        };

        return (
            <Grid container spacing={3}>
                {/* Tarjetas de Scores */}
                <Grid item xs={12} md={6}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                {t('evaluationResponses.scoreAssessment')}
                            </Typography>
                            <Typography variant="h3" sx={{ color: getScoreColor(overallScore), mb: 2 }}>
                                {overallScore.toFixed(1)}
                                <Typography component="span" variant="h5" color="text.secondary">
                                    {' '}/ 100
                                </Typography>
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={overallScore}
                                sx={{
                                    height: 8,
                                    borderRadius: 1,
                                    backgroundColor: 'grey.200',
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: getScoreColor(overallScore)
                                    }
                                }}
                            />
                        </CardContent>
                    </Card>
                </Grid>


                {/* Competencias Evaluadas */}
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                {t('evaluationResponses.evaluatedCompetencies')}
                            </Typography>

                            {candidateResponse?.questions && candidateResponse.questions.length > 0 ? (
                                (() => {
                                    // Helpers
                                    const unique = (arr) => [...new Set(arr)];
                                    const getCompName = (q) =>
                                        q?.competencyEvaluated || q?.competencyName || t('evaluationResponses.noCompetency');

                                    // Scores oficiales por competencia (raíz del documento).
                                    // Es la misma fuente que usan el detalle y los reportes.
                                    const rootScores = Array.isArray(candidateResponse?.competencyScores)
                                        ? candidateResponse.competencyScores
                                        : [];

                                    // 1) Agrupar por competencia
                                    const grouped = candidateResponse.questions.reduce((acc, q) => {
                                        const compName = getCompName(q);

                                        if (!acc[compName]) {
                                            acc[compName] = {
                                                competencyName: compName,
                                                competencyId: q?.competencyId || null,
                                                questions: [],
                                            };
                                        }

                                        acc[compName].questions.push(q);
                                        return acc;
                                    }, {});

                                    // 2) Convertir a lista + calcular score + deduplicar listas
                                    const competencyList = Object.values(grouped).map((c) => {
                                        // Preferir el score oficial; si no existe (assessments
                                        // antiguos) promediar los scores por pregunta INCLUYENDO
                                        // los ceros de las preguntas sin responder.
                                        const rootMatch = rootScores.find(
                                            (rs) => rs.competencyId && rs.competencyId === c.competencyId
                                        );

                                        const perQuestionScores = c.questions.map((q) => {
                                            const v = q?.competencyScore ?? q?.evaluationScore ?? 0;
                                            const n = Number(v);
                                            return Number.isNaN(n) ? 0 : n;
                                        });

                                        const avgScore = rootMatch && typeof rootMatch.competencyScore === 'number'
                                            ? Math.round(rootMatch.competencyScore)
                                            : (perQuestionScores.length
                                                ? Math.round(
                                                    perQuestionScores.reduce((a, b) => a + b, 0) / perQuestionScores.length
                                                )
                                                : 0);

                                        const strengths = unique(
                                            c.questions.flatMap((q) => q?.strengthsForPosition ?? [])
                                        );

                                        const improvements = unique(
                                            c.questions.flatMap((q) => q?.improvementAreas ?? [])
                                        );

                                        // Cobertura real: las preguntas sin responder puntúan 0
                                        // y no deben contarse como "evaluadas".
                                        const answeredCount = c.questions.filter(
                                            (q) => typeof q?.answerText === 'string' && q.answerText.trim() !== ''
                                        ).length;

                                        return {
                                            ...c,
                                            competencyScore: avgScore,
                                            answeredCount,
                                            totalCount: c.questions.length,
                                            strengths,
                                            improvements,
                                        };
                                    });

                                    // 3) Render
                                    return competencyList.map((competency, idx) => (
                                        <Box key={idx} sx={{ mb: 4 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    mb: 2,
                                                }}
                                            >
                                                <Typography variant="h6">{competency.competencyName}</Typography>

                                                <Chip
                                                    label={`${competency.competencyScore}/100`}
                                                    color={
                                                        competency.competencyScore >= 75
                                                            ? "success"
                                                            : competency.competencyScore >= 60
                                                                ? "warning"
                                                                : "error"
                                                    }
                                                    sx={{ fontWeight: "bold" }}
                                                />
                                            </Box>

                                            <Typography
                                                variant="caption"
                                                color={competency.answeredCount < competency.totalCount ? 'warning.main' : 'text.secondary'}
                                                display="block"
                                                sx={{ mb: 1 }}
                                            >
                                                {t('evaluationResponses.questionsAnswered', {
                                                    answered: competency.answeredCount,
                                                    total: competency.totalCount
                                                })}
                                            </Typography>

                                            {/* Fortalezas */}
                                            {competency.strengths.length > 0 && (
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography
                                                        variant="subtitle2"
                                                        color="success.main"
                                                        gutterBottom
                                                    >
                                                        {t('evaluationResponses.strengthsForPosition')}
                                                    </Typography>

                                                    <Box sx={{ ml: 2 }}>
                                                        {competency.strengths.map((strength, sIdx) => (
                                                            <Box
                                                                key={sIdx}
                                                                sx={{ display: "flex", gap: 1, mb: 0.5 }}
                                                            >
                                                                <Typography variant="body2">✓</Typography>
                                                                <Typography variant="body2">{strength}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}

                                            {/* Áreas de Mejora */}
                                            {competency.improvements.length > 0 && (
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        color="warning.main"
                                                        gutterBottom
                                                    >
                                                        {t('evaluationResponses.improvementAreas')}
                                                    </Typography>

                                                    <Box sx={{ ml: 2 }}>
                                                        {competency.improvements.map((area, aIdx) => (
                                                            <Box
                                                                key={aIdx}
                                                                sx={{ display: "flex", gap: 1, mb: 0.5 }}
                                                            >
                                                                <Typography variant="body2">⚠</Typography>
                                                                <Typography variant="body2">{area}</Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    ));
                                })()
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {t('evaluationResponses.noCompetenciesAvailable')}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Plan de Desarrollo */}
                {candidateResponse.plan_desarrollo && (
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    {t('evaluationResponses.developmentPlan90180360')}
                                </Typography>

                                {candidateResponse.plan_desarrollo.introduccion && (
                                    <Typography variant="body2" sx={{ mb: 3, fontStyle: 'italic', color: 'text.secondary' }}>
                                        {candidateResponse.plan_desarrollo.introduccion}
                                    </Typography>
                                )}

                                {/* Primeros 90 días */}
                                {candidateResponse.plan_desarrollo.primeros_90_dias && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                                            {t('evaluationResponses.first90Days')}
                                        </Typography>

                                        {candidateResponse.plan_desarrollo.primeros_90_dias.objetivos && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.primeros_90_dias.objetivos.map((obj, idx) => (
                                                        <li key={idx}><Typography variant="body2">{obj}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {candidateResponse.plan_desarrollo.primeros_90_dias.acciones && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.primeros_90_dias.acciones.map((acc, idx) => (
                                                        <li key={idx}><Typography variant="body2">{acc}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {candidateResponse.plan_desarrollo.primeros_90_dias.indicadores && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.primeros_90_dias.indicadores.map((ind, idx) => (
                                                        <li key={idx}><Typography variant="body2">{ind}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Siguientes 90 días */}
                                {candidateResponse.plan_desarrollo.siguientes_90_dias && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                                            {t('evaluationResponses.next90Days')}
                                        </Typography>

                                        {candidateResponse.plan_desarrollo.siguientes_90_dias.objetivos && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.siguientes_90_dias.objetivos.map((obj, idx) => (
                                                        <li key={idx}><Typography variant="body2">{obj}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {candidateResponse.plan_desarrollo.siguientes_90_dias.acciones && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.siguientes_90_dias.acciones.map((acc, idx) => (
                                                        <li key={idx}><Typography variant="body2">{acc}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {candidateResponse.plan_desarrollo.siguientes_90_dias.indicadores && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.siguientes_90_dias.indicadores.map((ind, idx) => (
                                                        <li key={idx}><Typography variant="body2">{ind}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}

                                {/* Tercer período */}
                                {candidateResponse.plan_desarrollo.tercer_periodo && (
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                                            {t('evaluationResponses.thirdPeriod')}
                                        </Typography>

                                        {candidateResponse.plan_desarrollo.tercer_periodo.objetivos && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.objectives')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.tercer_periodo.objetivos.map((obj, idx) => (
                                                        <li key={idx}><Typography variant="body2">{obj}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {candidateResponse.plan_desarrollo.tercer_periodo.acciones && (
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.actions')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.tercer_periodo.acciones.map((acc, idx) => (
                                                        <li key={idx}><Typography variant="body2">{acc}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}

                                        {candidateResponse.plan_desarrollo.tercer_periodo.indicadores && (
                                            <Box>
                                                <Typography variant="subtitle2" gutterBottom>
                                                    {t('evaluationResponses.indicators')}
                                                </Typography>
                                                <Box component="ul" sx={{ pl: 3, mt: 1 }}>
                                                    {candidateResponse.plan_desarrollo.tercer_periodo.indicadores.map((ind, idx) => (
                                                        <li key={idx}><Typography variant="body2">{ind}</Typography></li>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {/* Conclusión Integrada */}
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                {t('evaluationResponses.integratedConclusion')}
                            </Typography>

                            {candidateResponse.conclusion_integrada ? (
                                typeof candidateResponse.conclusion_integrada === 'string' ? (
                                    // Formato antiguo (string con párrafos)
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.7 }}>
                                        {candidateResponse.conclusion_integrada}
                                    </Typography>
                                ) : (
                                    // Formato nuevo (objeto estructurado)
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {candidateResponse.conclusion_integrada.vision_general && (
                                            <Box>
                                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                                    {t('evaluationResponses.generalPerformanceView')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                    {candidateResponse.conclusion_integrada.vision_general}
                                                </Typography>
                                            </Box>
                                        )}

                                        {candidateResponse.conclusion_integrada.oportunidades_desarrollo && (
                                            <Box>
                                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                                    {t('evaluationResponses.developmentOpportunities')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                    {candidateResponse.conclusion_integrada.oportunidades_desarrollo}
                                                </Typography>
                                            </Box>
                                        )}

                                        {candidateResponse.conclusion_integrada.sintesis_perfil && (
                                            <Box>
                                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                                    {t('evaluationResponses.profileSynthesis')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                    {candidateResponse.conclusion_integrada.sintesis_perfil}
                                                </Typography>
                                            </Box>
                                        )}

                                        {candidateResponse.conclusion_integrada.implicaciones_cargo && (
                                            <Box>
                                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                                    {t('evaluationResponses.positionImplications')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                                                    {candidateResponse.conclusion_integrada.implicaciones_cargo}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                )
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {t('evaluationResponses.noConclusionAvailable')}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    };

    // Utilitarias
    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'in_progress':
            case 'started': return 'warning';
            case 'expired': return 'error';
            default: return 'default';
        }
    };

    const getStatusText = (status, t) => {
        switch (status) {
            case 'completed':
                return t('evaluationResponses.statusCompleted');
            case 'in_progress':
            case 'started':
                return t('evaluationResponses.statusInProgress');
            case 'expired':
                return t('evaluationResponses.statusExpired');
            case 'pending':
                return t('evaluationResponses.statusPending');
            case 'sent':
                return t('evaluationResponses.statusSent');
            default:
                return t('evaluationResponses.statusUnknown');
        }
    };

    const formatTime = (minutes) => {
        if (!minutes || minutes === 0) return '0 min';
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return hours > 0 ? `${hours}h ${remainingMinutes}m` : `${remainingMinutes} min`;
    };

    const getScoreColor = (score) => {
        if (score >= 85) return 'success';
        if (score >= 70) return 'warning';
        return 'error';
    };

    const filteredCandidates = candidatesData.filter(candidate => {
        const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            candidate.email.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    // Candidatos seleccionados con datos completos
    const selectedCandidatesData = React.useMemo(() => {
        return completedCandidatesForRanking.filter(c => selectedCandidates.includes(c.id));
    }, [completedCandidatesForRanking, selectedCandidates]);

    // Competencias seleccionadas con datos completos
    const selectedCompetenciesData = React.useMemo(() => {
        return competenciesData.filter(c => selectedCompetencies.includes(c.id));
    }, [competenciesData, selectedCompetencies]);

    // Colores para los candidatos
    const chartColors = [
        '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1',
        '#d084d0', '#ffb347', '#87ceeb', '#dda0dd', '#98fb98'
    ];

    // Preparar datos para el radar chart
    const radarChartData = React.useMemo(() => {
        if (selectedCandidatesData.length === 0 || selectedCompetenciesData.length === 0) {
            return [];
        }

        return selectedCompetenciesData.map(competency => {
            const dataPoint = {
                competency: competency.name.length > 15
                    ? competency.name.substring(0, 15) + '...'
                    : competency.name,
                fullName: competency.name
            };

            selectedCandidatesData.forEach(candidate => {
                const score = candidate.competencyScores[competency.id] || 0;
                dataPoint[candidate.name] = Math.round(score);
            });

            return dataPoint;
        });
    }, [selectedCandidatesData, selectedCompetenciesData]);

    return (
        <Paper sx={{ borderRadius: 2 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab label={t('evaluationResponses.tabAllApplicants')} />
                    <Tab label={t('evaluationResponses.tabComparative')} />
                    <Tab label={t('evaluationResponses.tabBySkills')} />
                </Tabs>
            </Box>

            <Box sx={{ p: 3 }}>
                <TabPanel value={tabValue} index={0}>
                    {/* Lista completa de candidatos */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <TextField
                            placeholder={t('evaluationResponses.searchApplicant')}
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ minWidth: 300 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {t('evaluationResponses.applicantsFound', { count: filteredCandidates.length })}
                        </Typography>
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('evaluationResponses.applicant')}</TableCell>
                                    <TableCell>{t('evaluationResponses.status')}</TableCell>
                                    <TableCell align="center">{t('evaluationResponses.progress')}</TableCell>
                                    <TableCell align="center">{t('evaluationResponses.score')}</TableCell>
                                    <TableCell align="center">{t('evaluationResponses.time')}</TableCell>
                                    <TableCell align="center">{t('evaluationResponses.date')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredCandidates.map((candidate) => (
                                    <TableRow key={candidate.id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ width: 32, height: 32 }}>
                                                    {candidate.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {candidate.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {candidate.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getStatusText(candidate.status, t)}
                                                color={getStatusColor(candidate.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ minWidth: 120 }}>
                                                <ScoreBar
                                                    variant="determinate"
                                                    value={candidate.progress}
                                                    color={candidate.progress === 100 ? 'success' : 'primary'}
                                                />
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('evaluationResponses.questionsProgress', {
                                                        completed: candidate.completedQuestions,
                                                        total: candidate.totalQuestions
                                                    })}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                                color={getScoreColor(candidate.overallScore)}
                                            >
                                                {candidate.overallScore.toFixed(1)}%
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {formatTime(candidate.timeSpent)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2">
                                                {candidate.submittedAt
                                                    ? candidate.submittedAt.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')
                                                    : t('evaluationResponses.notAvailable')
                                                }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {/* Diseño de comparativa con selección */}
                    <Grid container spacing={3}>
                        {/* Panel izquierdo - Selección */}
                        <Grid item xs={12} md={4}>
                            <Paper sx={{ p: 3, height: 'fit-content' }}>
                                {/* Seleccionar Candidatos */}
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    {t('evaluationResponses.selectApplicants')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t('evaluationResponses.selectApplicantsDescription')}
                                </Typography>

                                <Box sx={{ mb: 3 }}>
                                    {completedCandidatesForRanking.map((candidate) => (
                                        <Box
                                            key={candidate.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 1,
                                                p: 1,
                                                borderRadius: 1,
                                                '&:hover': { backgroundColor: 'grey.50' }
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedCandidates.includes(candidate.id)}
                                                onChange={() => handleCandidateSelection(candidate.id)}
                                                size="small"
                                            />
                                            <Box sx={{ ml: 1 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {candidate.name}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {candidate.position}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ))}
                                </Box>

                                {/* Competencias a comparar */}
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    {t('evaluationResponses.competenciesToCompare')}
                                </Typography>
                                <Box>
                                    {competenciesData.map((competency) => (
                                        <Box
                                            key={competency.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                mb: 1,
                                                p: 1,
                                                borderRadius: 1,
                                                '&:hover': { backgroundColor: 'grey.50' }
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedCompetencies.includes(competency.id)}
                                                onChange={() => handleCompetencySelection(competency.id)}
                                                size="small"
                                            />
                                            <Typography variant="body2" sx={{ ml: 1 }}>
                                                {competency.name}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            </Paper>
                        </Grid>

                        {/* Panel derecho - Análisis Comparativo */}
                        <Grid item xs={12} md={8}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 1 }}>
                                    {t('evaluationResponses.comparativeAnalysis')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {t('evaluationResponses.comparativeAnalysisDescription')}
                                </Typography>

                                {/* Sub-tabs para Gráfico y Análisis */}
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                    <Tabs value={comparisonTabValue} onChange={handleComparisonTabChange}>
                                        <Tab label={t('evaluationResponses.comparativeChart')} />
                                        <Tab label={t('evaluationResponses.analysisByApplicant')} />
                                    </Tabs>
                                </Box>

                                {/* Contenido de comparación */}
                                <TabPanel value={comparisonTabValue} index={0}>
                                    {selectedCandidates.length >= 2 ? (
                                        <Box sx={{ width: '100%', height: 500 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <RadarChart data={radarChartData}>
                                                    <PolarGrid gridType="polygon" />
                                                    <PolarAngleAxis
                                                        dataKey="competency"
                                                        tick={{ fontSize: 12, fill: '#666' }}
                                                        className="radar-label"
                                                    />
                                                    <PolarRadiusAxis
                                                        angle={90}
                                                        domain={[0, 100]}
                                                        tick={{ fontSize: 10, fill: '#999' }}
                                                        tickFormatter={(value) => `${value}%`}
                                                    />
                                                    {selectedCandidatesData.map((candidate, index) => (
                                                        <Radar
                                                            key={candidate.id}
                                                            name={candidate.name}
                                                            dataKey={candidate.name}
                                                            stroke={chartColors[index]}
                                                            fill={chartColors[index]}
                                                            fillOpacity={0.1}
                                                            strokeWidth={2}
                                                            dot={{ fill: chartColors[index], strokeWidth: 1, r: 4 }}
                                                        />
                                                    ))}
                                                    <Legend
                                                        verticalAlign="bottom"
                                                        height={36}
                                                        iconType="line"
                                                        wrapperStyle={{ paddingTop: '20px' }}
                                                    />
                                                </RadarChart>
                                            </ResponsiveContainer>

                                            {/* Información adicional */}
                                            <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="body2" color="text.secondary" align="center">
                                                    {t('evaluationResponses.comparingInfo', {
                                                        applicants: selectedCandidates.length,
                                                        competencies: selectedCompetencies.length
                                                    })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ) : selectedCandidates.length === 1 ? (
                                        <Alert severity="info">
                                            {t('evaluationResponses.selectAtLeastTwoApplicants')}
                                        </Alert>
                                    ) : (
                                        <Alert severity="warning">
                                            {t('evaluationResponses.selectApplicantsForChart')}
                                        </Alert>
                                    )}
                                </TabPanel>

                                <TabPanel value={comparisonTabValue} index={1}>
                                    {selectedCandidates.length > 0 ? (
                                        <Grid container spacing={3}>
                                            {selectedCandidatesData.map((candidate) => (
                                                <Grid item xs={12} sm={6} key={candidate.id}>
                                                    <StyledCard>
                                                        <CardContent>
                                                            <Typography variant="h6" gutterBottom>
                                                                {candidate.name}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                                {candidate.position}
                                                            </Typography>

                                                            <Typography variant="h3" color={getScoreColor(candidate.overallScore)} sx={{ my: 2 }}>
                                                                {candidate.overallScore.toFixed(1)}%
                                                            </Typography>

                                                            {/* Desglose por competencias seleccionadas */}
                                                            {selectedCompetenciesData.map((competency) => {
                                                                const score = candidate.competencyScores[competency.id] || 0;
                                                                return (
                                                                    <Box key={competency.id} sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                        <Typography variant="body2">
                                                                            {competency.name}
                                                                        </Typography>
                                                                        <Typography variant="body2" fontWeight="medium">
                                                                            {score.toFixed(0)}%
                                                                        </Typography>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </CardContent>
                                                    </StyledCard>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    ) : (
                                        <Alert severity="info">
                                            {t('evaluationResponses.selectApplicantsForAnalysis')}
                                        </Alert>
                                    )}
                                </TabPanel>
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    {/* Dashboard por competencias */}
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t('evaluationResponses.analysisBySkills')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {t('evaluationResponses.analysisBySkillsDescription')}
                    </Typography>

                    {/* Promedios Generales - competencias con datos */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('evaluationResponses.generalAverages')}
                        </Typography>

                        {competenciesData.filter(comp => comp.totalResponses > 0).length > 0 ? (
                            competenciesData.filter(comp => comp.totalResponses > 0).map((competency) => (
                                <Box key={competency.id} sx={{ mb: 3 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body1" fontWeight="medium">
                                            {competency.name}
                                        </Typography>
                                        <Typography variant="body1" fontWeight="bold">
                                            {competency.averageScore.toFixed(1)}%
                                        </Typography>
                                    </Box>
                                    <CompetencyBar
                                        variant="determinate"
                                        value={Math.max(competency.averageScore, 1)} // Mínimo 1% para que se vea la barra
                                        color={getScoreColor(competency.averageScore)}
                                        sx={{ height: 12 }}
                                    />
                                </Box>
                            ))
                        ) : (
                            <Alert severity="info">
                                {t('evaluationResponses.averagesWillShow')}
                            </Alert>
                        )}
                    </Box>

                    {/* Ranking de Candidatos */}
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {t('evaluationResponses.applicantRanking')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {t('evaluationResponses.applicantRankingDescription')}
                        </Typography>

                        {completedCandidatesForRanking.map((candidate, index) => (
                            <Box key={candidate.id} sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                    <Typography variant="h6" sx={{ minWidth: 30 }}>
                                        {index + 1}
                                    </Typography>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body1" fontWeight="medium">
                                            {candidate.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {candidate.position}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {candidate.overallScore.toFixed(1)}%
                                    </Typography>
                                </Box>
                                <CompetencyBar
                                    variant="determinate"
                                    value={candidate.overallScore}
                                    color={getScoreColor(candidate.overallScore)}
                                    sx={{ height: 12 }}
                                />
                            </Box>
                        ))}
                    </Box>

                    {/* Tabla detallada por competencias - solo candidatos con scores válidos */}
                    {completedCandidatesForRanking.length > 0 ? (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <SortableTableCell column="candidate">
                                            {t('evaluationResponses.applicant')}
                                        </SortableTableCell>
                                        {competenciesData.map((competency) => (
                                            <SortableTableCell key={competency.id} column={competency.id} align="center">
                                                {competency.name}
                                            </SortableTableCell>
                                        ))}
                                        <SortableTableCell column="total" align="center">
                                            {t('evaluationResponses.total')}
                                        </SortableTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedCandidatesForTable.map((candidate) => (
                                        <TableRow key={candidate.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {candidate.name}
                                                </Typography>
                                            </TableCell>
                                            {competenciesData.map((competency) => {
                                                const score = candidate.competencyScores[competency.id] || 0;
                                                return (
                                                    <TableCell key={competency.id} align="center">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CompetencyBar
                                                                variant="determinate"
                                                                value={score}
                                                                color={getScoreColor(score)}
                                                                sx={{ flexGrow: 1, height: 8 }}
                                                            />
                                                            <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 45 }}>
                                                                {score >= 0 ? score.toFixed(1) + '%' : t('evaluationResponses.notAvailable')}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="center">
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <CompetencyBar
                                                        variant="determinate"
                                                        value={candidate.overallScore}
                                                        color={getScoreColor(candidate.overallScore)}
                                                        sx={{ flexGrow: 1, height: 8 }}
                                                    />
                                                    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 45 }}>
                                                        {candidate.overallScore.toFixed(1)}%
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            {t('evaluationResponses.detailedTableWillShow')}
                        </Alert>
                    )}

                    {/* Análisis y Plan de Desarrollo */}
                    {completedCandidatesForRanking.length > 0 && (
                        <Box sx={{ mt: 6 }}>
                            <Typography variant="h6" sx={{ mb: 2 }}>
                                {t('evaluationResponses.analysisAndDevelopmentPlan')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                {t('evaluationResponses.personalizedRecommendations')}
                            </Typography>

                            {/* Tabs por candidato */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs value={candidateTabValue} onChange={handleCandidateTabChange} variant="scrollable" scrollButtons="auto">
                                    {completedCandidatesForRanking.map((candidate, index) => (
                                        <Tab key={candidate.id} label={candidate.name} />
                                    ))}
                                </Tabs>
                            </Box>

                            {/* Contenido por candidato */}
                            {completedCandidatesForRanking.map((candidate, index) => (
                                <TabPanel key={candidate.id} value={candidateTabValue} index={index}>
                                    {renderCandidateAnalysis(candidate)}
                                </TabPanel>
                            ))}
                        </Box>
                    )}
                </TabPanel>
            </Box>
        </Paper>
    );
};

export default EvaluationComparisonTabs;